import Button from '@ingka/button';
import SSRIcon from '@ingka/ssr-icon';
import arrowCloudInPath from '@ingka/ssr-icon/paths/arrow-cloud-in';
import arrowLeftPath from '@ingka/ssr-icon/paths/arrow-left';
import crossCirclePath from '@ingka/ssr-icon/paths/cross-circle';
import documentUploadPath from '@ingka/ssr-icon/paths/document-upload';
import magnifyingGlassPath from '@ingka/ssr-icon/paths/magnifying-glass';
import noticeRedPath from '@ingka/ssr-icon/paths/notice-red';
import Text from '@ingka/text';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { completeUpload, getSasToken } from '@/api/client';
import { useApp } from '@/context/AppContext';
import { ACCEPT_ATTR, ALLOWED_FILE_EXTENSIONS, MAX_FILE_SIZE_BYTES } from '@/data/constants';
import { useClassificationName, useEntityName, useProductName } from '@/hooks/useCatalogNames';
import { fmtSize } from '@/utils/formatters';
import { countFileRows } from '@/utils/row-count';
import styles from './UploadStep.module.css';

type Phase = 'idle' | 'uploading' | 'done' | 'error';

interface ValidationError {
  title: string;
  message: string;
}

export default function UploadStep() {
  const { product, entity, classification, user, setLastUpload } = useApp();
  const navigate = useNavigate();
  const productLabel = useProductName(product);
  const entityLabel = useEntityName(product, entity);
  const classificationLabel = useClassificationName(classification);

  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState<ValidationError | null>(null);
  const [rowCount, setRowCount] = useState<number | null>(null);
  const [rowCountLoading, setRowCountLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // ── File validation ────────────────────────────────────────────────────────
  const validateFile = (f: File): ValidationError | null => {
    const dot = f.name.lastIndexOf('.');
    const ext = dot > 0 ? f.name.slice(dot + 1).toLowerCase() : '';
    if (!ext || !(ALLOWED_FILE_EXTENSIONS as readonly string[]).includes(ext)) {
      return {
        title: 'Wrong file type',
        message: `File type ".${ext || '(none)'}" is not allowed. Allowed types: ${ALLOWED_FILE_EXTENSIONS.map((e) => `.${e}`).join(', ')}.`,
      };
    }
    if (f.size > MAX_FILE_SIZE_BYTES) {
      return {
        title: 'File too large',
        message: `File is too large (${fmtSize(f.size)}). Maximum allowed size is 500 MB.`,
      };
    }
    return null;
  };

  const pickFile = (f: File) => {
    const err = validateFile(f);
    if (err) {
      setErrorMsg(err);
      return;
    }
    setErrorMsg(null);
    setFile(f);
    setRowCount(null);
    setRowCountLoading(true);
    // Row counting is best-effort and can take a moment on large XLSX files.
    // We don't block the UI; the review screen shows "counting…" until it settles.
    void countFileRows(f)
      .then((count) => setRowCount(count))
      .finally(() => setRowCountLoading(false));
  };

  const resetFile = () => {
    setFile(null);
    setErrorMsg(null);
    setRowCount(null);
    setRowCountLoading(false);
  };

  // ── Drag-and-drop ──────────────────────────────────────────────────────────
  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const onDragLeave = useCallback(() => setDragActive(false), []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pickFile is unmemoized but only reads constants, so a stale reference is harmless; adding it would recreate onDrop every render.
  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) pickFile(dropped);
  }, []);

  // ── Upload ─────────────────────────────────────────────────────────────────
  const startUpload = async () => {
    if (!file || !product || !entity || !classification || !user) return;
    setPhase('uploading');
    setProgress(0);
    setErrorMsg(null);

    try {
      // 1. Get SAS token from the BFF API (cookie sent automatically)
      const { sasUrl, uploadId } = await getSasToken(product, entity, classification, file.name);

      // 2. Upload directly to ADLS Gen2 using the SAS URL
      const { BlockBlobClient } = await import('@azure/storage-blob');
      const blockBlobClient = new BlockBlobClient(sasUrl);
      const uploadResponse = await blockBlobClient.uploadData(file, {
        blockSize: 4 * 1024 * 1024,
        concurrency: 4,
        onProgress: (ev) => {
          setProgress(Math.round((ev.loadedBytes / file.size) * 100));
        },
        blobHTTPHeaders: { blobContentType: file.type || 'application/octet-stream' },
      });

      // Capture the blob version ID returned by storage when versioning is enabled.
      const blobVersionId: string | undefined = uploadResponse.versionId ?? undefined;

      // 3. Tell the API the upload is complete (updates Postgres status).
      //    Layer-1 retry: up to 3 attempts with exponential backoff.
      //    If all retries fail the file IS already in storage — we still show success
      //    and rely on Layer-2 (Event Grid) to reconcile the DB row.
      const markComplete = async (retries = 3): Promise<void> => {
        for (let i = 0; i < retries; i++) {
          try {
            await completeUpload(uploadId, file.size, blobVersionId, rowCount);
            return;
          } catch {
            if (i < retries - 1) {
              await new Promise<void>((r) => setTimeout(r, 1000 * (i + 1)));
            }
          }
        }
        // All retries failed — blob is in storage, Layer-2 will repair the DB row.
        console.warn('[upload] /complete failed after retries — Layer-2 will reconcile', uploadId);
      };

      await markComplete();

      setLastUpload({
        id: uploadId,
        fileName: file.name,
        product,
        entity,
        classification,
        sizeBytes: file.size,
        uploadedAt: new Date().toISOString(),
        status: 'uploaded',
        uploadedBy: user.sub,
      });

      setPhase('done');
      navigate('/upload/success');
    } catch (err: unknown) {
      setPhase('error');
      const message = err instanceof Error ? err.message : 'Upload failed. Please try again.';
      setErrorMsg({ title: 'Upload failed', message });
    }
  };

  const goBack = () => navigate('/upload/classification');

  // Format the row count value for display in the confirm card.
  const rowCountDisplay = rowCountLoading
    ? 'Counting…'
    : rowCount === null
      ? 'Not available'
      : rowCount === 0
        ? '0 (file appears empty)'
        : rowCount.toLocaleString();

  return (
    <div className={styles.page}>
      <Button
        type="tertiary"
        size="small"
        text="Back to classification"
        ssrIcon={arrowLeftPath}
        iconPosition="leading"
        onClick={goBack}
        disabled={phase === 'uploading'}
        className={styles.backBtn}
      />

      <div className={styles.hero}>
        <Text tagName="h2" textStyle="Heading.XL" className={styles.heroTitle}>
          Upload your file
        </Text>
        <Text tagName="p" textStyle="Body.L" className={styles.heroBody}>
          {file
            ? 'Review the details below before confirming.'
            : 'Drag your file into the box below, or browse from your computer. Maximum size 500 MB.'}
        </Text>
      </div>

      {/* Error */}
      {errorMsg && (
        <div className={styles.errorWrap}>
          <div className={styles.errorBanner}>
            <SSRIcon paths={noticeRedPath} className={styles.errorIcon} />
            <div>
              <span className={styles.errorTitle}>{errorMsg.title}</span>
              <span className={styles.errorBody}>{errorMsg.message}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Drop zone — hidden once a file is picked ──────────────────────── */}
      {!file && (
        // biome-ignore lint/a11y/useSemanticElements: can't be a real <button> — it wraps a nested "Choose file" button, and buttons can't contain buttons.
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="File drop zone"
          className={`${styles.dropZone}${dragActive ? ` ${styles.dropZoneDragging}` : ''}`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT_ATTR}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) pickFile(f);
            }}
            style={{ display: 'none' }}
            aria-hidden
          />
          <div className={`${styles.dropIcon}${dragActive ? ` ${styles.dropIconDragging}` : ''}`}>
            <SSRIcon paths={documentUploadPath} />
          </div>
          <Text tagName="p" textStyle="Body.L" className={styles.dropTitle}>
            {dragActive ? 'Drop your file here' : 'Drag a file here'}
          </Text>
          <Text tagName="p" textStyle="Body.M" className={styles.dropSubtitle}>
            or click anywhere in this box to browse
          </Text>
          <Button
            type="emphasised"
            text="Choose file"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
          />
        </div>
      )}

      {/* ── Upload progress — shown while uploading ────────────────────────── */}
      {file && phase === 'uploading' && (
        <div className={styles.progressCard}>
          <div className={styles.progressFile}>
            <div className={styles.progressFileIcon}>📄</div>
            <div className={styles.progressFileMeta}>
              <Text tagName="p" textStyle="Body.L" className={styles.progressFileName}>
                {file.name}
              </Text>
              <Text tagName="p" textStyle="Body.S" className={styles.progressFileSize}>
                {fmtSize(file.size)}
              </Text>
            </div>
          </div>
          <div className={styles.progressTrack}>
            <div className={styles.progressBar} style={{ width: `${progress}%` }} />
          </div>
          <div className={styles.progressLabels}>
            <span>Uploading…</span>
            <strong className={styles.progressPercent}>{progress}%</strong>
          </div>
        </div>
      )}

      {/* ── Confirmation — replaces drop zone when file is picked ─────────── */}
      {file && phase === 'idle' && (
        <div className={styles.confirmCard}>
          <Text tagName="p" textStyle="Heading.S" className={styles.confirmTitle}>
            Please confirm your upload
          </Text>
          {(
            [
              { label: 'File name', value: file.name },
              { label: 'Product', value: productLabel || '—' },
              { label: 'Entity', value: entityLabel || '—' },
              { label: 'Classification', value: classificationLabel || '—' },
              { label: 'Size', value: fmtSize(file.size) },
              { label: 'Rows', value: rowCountDisplay },
            ] as { label: string; value: string }[]
          ).map(({ label, value }, i, arr) => (
            <div
              key={label}
              className={`${styles.confirmRow}${i < arr.length - 1 ? ` ${styles.confirmRowBorder}` : ''}`}
            >
              <Text tagName="span" textStyle="Body.M" className={styles.confirmLabel}>
                {label}
              </Text>
              <Text tagName="span" textStyle="Body.M" className={styles.confirmValue}>
                {value}
              </Text>
            </div>
          ))}
        </div>
      )}

      {/* ── Upload actions ─────────────────────────────────────────────────── */}
      {file && phase === 'idle' && (
        <div className={styles.actions}>
          <Button
            type="emphasised"
            text={rowCountLoading ? 'Counting rows…' : 'Upload file'}
            ssrIcon={arrowCloudInPath}
            iconPosition="leading"
            disabled={rowCountLoading}
            onClick={() => void startUpload()}
          />
          <Button
            type="primary"
            ssrIcon={magnifyingGlassPath}
            iconPosition="leading"
            text="Choose a different file"
            onClick={resetFile}
          />
          <Button
            type="danger"
            text="Cancel"
            iconPosition="leading"
            ssrIcon={crossCirclePath}
            onClick={() => {
              resetFile();
              navigate('/upload/product');
            }}
          />
        </div>
      )}
    </div>
  );
}